import { useTranslation } from "@sagot/i18n";
import { Button, Icon, initialsOf, toast } from "@sagot/ui";
import { useRef } from "react";
import { useUploadErrorMessage } from "../../../lib/upload-errors";
import { imageProblem } from "./brand-image";
import { useBrandImageUpload } from "./use-widget-studio";
import { AVATAR_MIME_TYPES } from "./widget-model";

interface AvatarUploaderProps {
  agentId: string;
  agentName: string;
  avatarUrl: string | null;
  useAgentAvatar: boolean;
  onChange: (next: { avatar_url: string | null; use_agent_avatar: boolean }) => void;
  disabled?: boolean;
}

export function isAcceptedAvatar(file: Pick<File, "type" | "size"> & { name?: string }): boolean {
  return imageProblem(file) === null;
}

/** Widget avatar preview + upload (PNG/JPEG/WebP ≤ 2 MB) or "Use agent avatar". */
export function AvatarUploader({ agentId, agentName, avatarUrl, useAgentAvatar, onChange, disabled }: AvatarUploaderProps) {
  const { t } = useTranslation();
  const message = useUploadErrorMessage();
  const input = useRef<HTMLInputElement>(null);
  const upload = useBrandImageUpload(agentId);
  const showImage = !useAgentAvatar && avatarUrl;

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (!isAcceptedAvatar(file)) {
      toast.error(t("deployKnowledge.widgetStudio.appearance.avatarInvalid"));
      return;
    }
    upload.mutate(file, {
      onSuccess: ({ url }) => {
        onChange({ avatar_url: url, use_agent_avatar: false });
        toast.success(t("deployKnowledge.widgetStudio.appearance.avatarUploaded", { name: file.name }));
      },
      onError: (error) => toast.error(message(error, t("deployKnowledge.widgetStudio.appearance.avatarFailed"))),
    });
  };

  return (
    <div>
      <span className="label">{t("deployKnowledge.widgetStudio.appearance.avatar")}</span>
      <div className="flex flex-wrap items-center gap-3">
        {showImage ? (
          <img src={avatarUrl} alt={t("deployKnowledge.widgetStudio.appearance.avatarAlt")} className="size-10 rounded-pill object-cover" />
        ) : (
          <span aria-hidden="true" className="avatar size-10 bg-ink-950 text-xs font-extrabold text-brand-500 dark:bg-brand-500 dark:text-ink-950">
            {initialsOf(agentName)}
          </span>
        )}
        <input
          ref={input}
          type="file"
          data-upload="avatar"
          accept={AVATAR_MIME_TYPES.join(",")}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => {
            handleFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <Button variant="secondary" size="sm" disabled={disabled} loading={upload.isPending} onClick={() => input.current?.click()}>
          <Icon name="upload" />
          {upload.isPending ? t("deployKnowledge.widgetStudio.appearance.uploading") : t("deployKnowledge.widgetStudio.appearance.upload")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={(disabled ?? false) || useAgentAvatar}
          onClick={() => {
            onChange({ avatar_url: null, use_agent_avatar: true });
            toast.info(t("deployKnowledge.widgetStudio.appearance.avatarReset"));
          }}
        >
          {t("deployKnowledge.widgetStudio.appearance.useAgentAvatar")}
        </Button>
      </div>
      <p className="hint">{t("deployKnowledge.widgetStudio.appearance.avatarHint")}</p>
    </div>
  );
}
